/* Итог товарных экранов: читаем только то, что вернул GET /app/orders/{id} (result.assets, sections, slides, caption)
   и файлы owner-protected маршрута /app/orders/{id}/result/files/{index}?revision=. Ссылки поставщика не используются. */
import { useEffect, useState } from 'react';
import { endpoints } from '../../api';
import type { SpeechOrder } from '../../api/speech';
import { record, rows, text } from '../studio/studio-content';

export interface ResultSlide {
  id: number;
  title: string;
  body: string;
  text: string;
  revision: number;
}
export interface ResultSection {
  kind: string;
  indices: number[];
  downloadUrl: string;
}
export interface ResultAssetMeta {
  index: number;
  mime: string;
  name: string;
  width?: number;
  height?: number;
  durationMs?: number;
  hasAudio?: boolean;
}
export interface ProductResultShape {
  assets: ResultAssetMeta[];
  sections: ResultSection[];
  slides: ResultSlide[];
  caption: string;
  archiveUrl: string;
}
const int = (value: unknown) => (Number.isSafeInteger(value) ? Number(value) : undefined);
/** Разбор результата без догадок: чего нет в ответе — того нет на экране. */
export function productResult(order: SpeechOrder | null | undefined): ProductResultShape {
  const result = record(order?.result);
  const assets = rows(result.assets)
    .map(asset => ({
      index: int(asset.index) ?? -1,
      mime: text(asset.mime),
      name: text(asset.file_name),
      width: int(asset.width),
      height: int(asset.height),
      durationMs: int(asset.duration_ms),
      hasAudio: typeof asset.has_audio === 'boolean' ? asset.has_audio : undefined,
    }))
    .filter(asset => asset.index >= 0 && asset.mime);
  const known = new Set(assets.map(asset => asset.index));
  const sections = rows(result.sections)
    .map(section => ({
      kind: text(section.kind),
      indices: (Array.isArray(section.asset_indices) ? section.asset_indices : []).filter(
        (value): value is number => Number.isSafeInteger(value) && known.has(value as number),
      ),
      downloadUrl: text(section.download_url),
    }))
    .filter(section => section.kind && section.indices.length);
  const slides = rows(result.slides)
    .map(slide => ({
      id: int(slide.id) ?? -1,
      title: text(slide.title),
      body: text(slide.body),
      text: text(slide.text),
      revision: int(slide.revision) ?? 1,
    }))
    .filter(slide => slide.id >= 0);
  return { assets, sections, slides, caption: text(result.caption), archiveUrl: text(result.archive_url) };
}

export interface LoadedAsset extends ResultAssetMeta {
  url: string;
  blob: Blob;
  text?: string;
}
const MAX_ASSETS = 40,
  MAX_BYTES = 200 * 1024 * 1024;
/** Загружаем все файлы итога (фото, слайды, видео, текст) — по секциям их раскладывает экран. */
export function useProductAssets(order: SpeechOrder | null | undefined) {
  const shape = productResult(order);
  const ready = order?.status === 'done' && order.result?.available && shape.assets.length > 0;
  const [attempt, setAttempt] = useState(0);
  const identity = ready ? `${order.id}:${order.result_revision}:${attempt}` : '';
  const [state, setState] = useState<{ identity: string; items: LoadedAsset[]; error: boolean }>({ identity: '', items: [], error: false });
  useEffect(() => {
    if (!identity || !order) return;
    const request = new AbortController();
    const urls: string[] = [];
    let disposed = false;
    const timer = setTimeout(() => request.abort(), 120000);
    void (async () => {
      const metas = productResult(order).assets;
      if (metas.length > MAX_ASSETS) throw new Error('result_too_large');
      const items: LoadedAsset[] = [];
      for (const meta of metas) {
        const blob = await endpoints.speech.result(order, meta.index, request.signal);
        if (disposed || request.signal.aborted) return;
        if (!blob.size || blob.size > MAX_BYTES) throw new Error('result_asset_invalid');
        const url = URL.createObjectURL(blob);
        urls.push(url);
        items.push({ ...meta, url, blob, ...(meta.mime.startsWith('text/') ? { text: await blob.text() } : {}) });
      }
      if (!disposed && !request.signal.aborted) setState({ identity, items, error: false });
    })()
      .catch(() => {
        if (!disposed) setState({ identity, items: [], error: true });
      })
      .finally(() => clearTimeout(timer));
    return () => {
      disposed = true;
      request.abort();
      clearTimeout(timer);
      urls.forEach(url => URL.revokeObjectURL(url));
    };
    // Набор файлов определяется заказом и его ревизией результата.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity]);
  const current = state.identity === identity;
  return {
    shape,
    items: current ? state.items : [],
    loading: !!identity && !current,
    error: !!identity && current && state.error,
    retry: () => setAttempt(value => value + 1),
  };
}

/** Скачивание файла или архива секции: только owner-protected пути заказа. */
export async function downloadArchive(order: SpeechOrder, url: string) {
  return endpoints.history.download(order.id, url);
}
export function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}
